import React, { ChangeEvent, FC, FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { createStyles, IconButton, makeStyles, TextField, Theme } from '@material-ui/core';
import Send from '@material-ui/icons/Send';

interface ComposerProps {
  onSend: (content: string) => Promise<void>;
  initialMessage: string;
}

const Composer: FC<ComposerProps> = ({ onSend, initialMessage }) => {
  const classes = useStyles();

  const input = useRef<HTMLInputElement>(null);

  const [busy, setBusy] = useState(false);
  const [content, setContent] = useState('');

  useEffect(() => {
    if (initialMessage !== null) {
      setContent(initialMessage);
    }
  }, [initialMessage]);

  const send = async () => {
    if (busy || !content.trim()) {
      return;
    }

    try {
      setBusy(true);
      await onSend(content.trim());
      setContent('');
    } catch (e) {
      // TODO
    } finally {
      setBusy(false);
      input.current?.focus();
    }
  };

  const handleClick = (e: FormEvent<HTMLButtonElement>) => {
    e.preventDefault();
    send().then(r => r);
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    send().then(r => r);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setContent(e.target.value);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key.toLowerCase() === 'enter' && e.metaKey) {
      send().then(r => r);
    }
  };

  useEffect(() => {
    if (input.current && document.activeElement !== input.current) {
      input.current.focus();
    }
  }, []);

  return (
    <form onSubmit={handleSubmit} className={classes.form}>
      <TextField
        multiline
        variant="outlined"
        color="secondary"
        placeholder="Type a message..."
        inputRef={input}
        inputProps={{
          'aria-autocomplete': 'none',
          'aria-haspopup': 'false',
          'aria-label': 'Compose message',
        }}
        autoCapitalize="off"
        autoComplete="off"
        autoCorrect="off"
        autoFocus
        contentEditable="true"
        role="textbox"
        spellCheck={false}
        value={content}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={busy}
        className={classes.inputField}
      />
      <IconButton
        type="submit"
        color="secondary"
        disabled={busy || !content.trim()}
        onClick={handleClick}
        className={classes.sendButton}
      >
        <Send />
      </IconButton>
    </form>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    inputField: {
      flex: 1,
      '& .MuiOutlinedInput-root': {
        borderRadius: theme.spacing(3),
        fontSize: '0.9375rem',
      },
    },
    form: {
      padding: theme.spacing(1.5, 2),
      display: 'flex',
      alignItems: 'flex-end',
      gap: theme.spacing(1),
      width: '100%',
      borderTop: `1px solid ${theme.palette.divider}`,
    },
    sendButton: {
      backgroundColor: theme.palette.secondary.main,
      color: '#fff',
      width: 40,
      height: 40,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
      },
      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        color: theme.palette.action.disabled,
      },
    },
  })
);

export default Composer;
